import { Text, Box, Center, Button, Flex } from '@chakra-ui/react'
import { AvatarCircle } from '@/components/atoms/Avatar'
import { useSession } from '@/context/SessionProvider'
import { useGetCurrentUserProfile } from '@/api/query'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next/pages'
import Link from 'next/link'
import { useAppToast } from '@/hooks/useAppToast'
import type { Exception } from '@injectivelabs/exceptions'
import { StageTitle } from './StageTitle'

interface ModalProps {
  onClose: () => void
}

export const ExpiryModalTemplate = ({ onClose }: ModalProps) => {
  const { t } = useTranslation()
  const { account, logout, refreshSession } = useSession()

  const { onSessionException, onTransactionError } = useAppToast()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: refreshSession,
    onSuccess: (result) => {
      if (result.error) {
        onSessionException(result.error)
      }
      if (result.data) {
        onClose()
      }
    },
    onError: (error: unknown) => {
      onTransactionError(error as Exception)
    },
  })

  const handleLogout = () => {
    logout()
    onClose()
  }

  const { data } = useGetCurrentUserProfile()

  return (
    <Flex h={'full'} flexDir="column" alignItems="center" justifyContent={'space-between'}>
      <StageTitle title={t('messages.renew-session')} />
      <Box>
        <Center mb="4">
          <AvatarCircle offset={8} size="2xl" src={data?.profile?.avatar} />
        </Center>
        <Center mb={0.5} pb="1">
          <Text fontWeight="700" fontSize="xl">
            {data?.profile?.user_name}
          </Text>
        </Center>
        <Center pb="4">
          <Text bg={'ninja.500'} px={1.5} py={0.5} rounded={'lg'} fontWeight="700" fontSize="xs" color="ninja.200">
            {account?.ethAddress}
          </Text>
        </Center>
      </Box>
      <Box>
        <Button borderRadius="xl" mb="2" w="full" isLoading={isPending} onClick={() => mutateAsync()}>
          {t('common.button.renew-session')}
        </Button>
        <Button variant="secondary" borderRadius="xl" mb="2" w="full" onClick={handleLogout} isDisabled={isPending}>
          {t('common.button.logout')}
        </Button>
        <Center>
          <Text fontSize="xs" color="ninja.300">
            {t('common:signin.privacy-policy-notice')}{' '}
            <Link href="https://docs.blaze.ninja/additional/terms-and-conditions" target="_blank">
              <Text color={'ninja.200'} textDecor={'underline'} as={'span'}>
                {t('common:common.terms-and-conditions')}
              </Text>
            </Link>
            .
          </Text>
        </Center>
      </Box>
    </Flex>
  )
}
